/**
 * iter-169zze — Reusable form-UX primitives.
 *
 * Audit findings from the existing 59 forms:
 *
 *   • Validation feedback is inconsistent — some forms toast errors,
 *     others show inline messages, others wait until submit.
 *   • Submit buttons frequently don't disable during the in-flight
 *     request, leading to duplicate POSTs on slow networks.
 *   • Backend errors arrive as ``e?.response?.data?.detail`` and get
 *     toasted as opaque strings, hiding field-specific guidance.
 *   • Mobile keyboards default to alpha for amount/phone/email
 *     inputs because we forgot ``inputMode``.
 *   • Labels are decorated <Label> elements but no ``htmlFor`` / ``id``
 *     pairing, so screen readers + click-on-label don't always work.
 *   • Auto-complete is missing on auth flows.
 *
 * This module provides four primitives that fix all of the above in
 * one place:
 *
 *   1. ``useFormUX(initial, schema?)`` — tiny state hook (no
 *      dependencies) tracking values + errors + touched + submitting.
 *   2. ``<FormField/>`` — wraps a labelled input with inline error +
 *      hint + ``aria-invalid`` / ``aria-describedby`` plumbing.
 *   3. ``<SubmitButton/>`` — auto-disable, spinner, idempotent.
 *   4. ``parseApiError(err, fieldMap?)`` — axios → field-error map
 *      with a sensible fallback string.
 *
 * Forms adopt incrementally: keep your existing ``useState`` flow,
 * just replace the bits you want hardened.
 */
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { reportValidationError } from "../lib/validationTelemetry";
import { Textarea } from "./ui/textarea";


// ── parseApiError ─────────────────────────────────────────────────────


/**
 * Normalise a thrown axios / fetch error into ``{ fieldErrors, message }``.
 *
 *   @param err       — the raw error caught from ``await api.post(...)``.
 *   @param fieldMap  — optional backend-key → form-field rename map.
 *                      e.g. ``{ email: "email", password_hash: "password" }``
 *                      Lets the backend's ``loc: ["body", "password_hash"]``
 *                      land on the form's ``password`` field.
 *
 * Recognises three response shapes:
 *
 *   • FastAPI Pydantic ``{ detail: [ {loc, msg, type}, … ] }``      → field errors.
 *   • Flat detail       ``{ detail: "Cuenta no encontrada" }``     → fallback message.
 *   • Field-keyed dict  ``{ email: "ya existe" }``                  → field errors.
 */
export function parseApiError(err, fieldMap = {}) {
  const data = err?.response?.data;
  const fieldErrors = {};
  let message = null;

  if (!data) {
    message = err?.message || "Network error";
    return { fieldErrors, message };
  }

  // Pydantic-style array of validation errors.
  if (Array.isArray(data.detail)) {
    for (const v of data.detail) {
      const lastLoc = Array.isArray(v.loc) ? v.loc[v.loc.length - 1] : null;
      const key = fieldMap[lastLoc] ?? lastLoc;
      if (key && typeof key === "string") {
        fieldErrors[key] = v.msg || "Invalid value";
      }
    }
    if (!Object.keys(fieldErrors).length) {
      message = "Datos inválidos. Revisa los campos.";
    }
    return { fieldErrors, message };
  }

  // Flat detail string.
  if (typeof data.detail === "string") {
    message = data.detail;
    return { fieldErrors, message };
  }

  // Field-keyed object {email: "...", password: "..."}.
  if (data && typeof data === "object") {
    for (const [k, v] of Object.entries(data)) {
      if (typeof v === "string") fieldErrors[fieldMap[k] ?? k] = v;
    }
    if (!Object.keys(fieldErrors).length) {
      message = JSON.stringify(data).slice(0, 200);
    }
    return { fieldErrors, message };
  }

  return { fieldErrors, message: String(data).slice(0, 200) };
}


// ── useFormUX ─────────────────────────────────────────────────────────


/**
 * Tiny form-state hook. Tracks:
 *   • ``values``      — current field values (controlled).
 *   • ``errors``      — field-keyed error strings (set by validate() or backend).
 *   • ``touched``     — fields the user has interacted with (errors hide before that).
 *   • ``submitting``  — true while ``submit(...)`` is awaiting.
 *   • ``submitCount`` — bumps each submit; force-show-all-errors uses this.
 *
 * Public API:
 *   • ``getFieldProps(name)`` — spread onto an <input/Input> to wire value+onChange+onBlur.
 *   • ``setValue(name, val)``
 *   • ``setError(name, msg)`` / ``setErrors(map)`` / ``clearError(name)``
 *   • ``handleSubmit(async (values) => …, options)`` — wraps an async submit
 *       handler with try/catch, sets ``submitting``, parses API errors,
 *       and resets errors. Returns an event handler suitable for ``onSubmit``.
 *   • ``reset(nextValues?)`` — restore to initial / new values + wipe errors.
 *
 * Validation: ``options.validate`` accepts either:
 *   • A function ``(values) → { fieldErrors }`` (legacy contract).
 *   • A Zod schema (anything with a ``.safeParse`` method). When the
 *     schema fails we flatten ``zError.issues`` into a ``{ field: msg }``
 *     map using the last segment of each issue's ``path``.
 *
 * Errors set inside ``handleSubmit`` block submit.
 */
export function useFormUX(initialValues, options = {}) {
  const { validate, fieldMap, formId } = options;
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitCount, setSubmitCount] = useState(0);

  const setValue = useCallback((name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    // Clear the field's error as the user types — feels less punitive.
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  }, []);

  const markTouched = useCallback((name) => {
    setTouched((prev) => (prev[name] ? prev : { ...prev, [name]: true }));
  }, []);

  const setError = useCallback((name, msg) => {
    setErrors((prev) => ({ ...prev, [name]: msg }));
  }, []);
  const clearError = useCallback((name) => {
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const reset = useCallback((next) => {
    setValues(next || initialValues);
    setErrors({});
    setTouched({});
    setSubmitCount(0);
  }, [initialValues]);

  /**
   * Wraps an async submit handler. Usage:
   *
   *   <form onSubmit={form.handleSubmit(async (values) => {
   *     await api.post("/x", values);
   *   })}>
   *
   * Options:
   *   • ``fieldMap`` — overrides hook-level fieldMap for this submit.
   *   • ``onError`` — called with ({fieldErrors, message}) if the
   *       submit throws (handler can decide to toast, etc.).
   *   • ``onSuccess`` — called with handler's return value.
   */
  const handleSubmit = useCallback((handler, { onError, onSuccess, fieldMap: localMap } = {}) => {
    return async (e) => {
      if (e?.preventDefault) e.preventDefault();
      setSubmitCount((n) => n + 1);
      // Mark all fields touched so errors become visible.
      setTouched(() => {
        const all = {};
        Object.keys(values).forEach((k) => { all[k] = true; });
        return all;
      });

      // Client-side validation pass. Supports two contracts:
      //   1. A plain function: `validate(values) → { fieldErrors }`.
      //   2. A Zod schema (has `.safeParse`): we run it and flatten the
      //      issues into a field→message map using the LAST path segment.
      if (validate) {
        let fe = {};
        if (typeof validate.safeParse === "function") {
          // Zod path. We require `safeParse` so we never throw inside
          // the submit pipeline.
          const result = validate.safeParse(values);
          if (!result.success) {
            for (const issue of result.error.issues || []) {
              const last = Array.isArray(issue.path) && issue.path.length
                ? issue.path[issue.path.length - 1]
                : null;
              if (last != null) {
                const key = String(last);
                // First issue wins per field — Zod can fire multiple
                // (e.g. min + custom refine). Surfacing only one keeps
                // the inline error short and stops the UI from jumping.
                if (!fe[key]) fe[key] = issue.message || "Invalid value";
              }
            }
          }
        } else if (typeof validate === "function") {
          const v = validate(values);
          fe = v?.fieldErrors || {};
        }
        if (Object.keys(fe).length) {
          setErrors(fe);
          // iter-169zze · fire-and-forget telemetry. The reporter dedups
          // per-session so spamming submit doesn't flood the collection.
          if (formId) reportValidationError(formId, fe);
          return;
        }
      }

      setErrors({});
      setSubmitting(true);
      try {
        const result = await handler(values);
        if (onSuccess) onSuccess(result);
      } catch (err) {
        const { fieldErrors, message } = parseApiError(err, localMap || fieldMap || {});
        if (Object.keys(fieldErrors).length) {
          setErrors(fieldErrors);
        }
        if (onError) onError({ fieldErrors, message, err });
      } finally {
        setSubmitting(false);
      }
    };
  }, [values, validate, fieldMap]);

  const getFieldProps = useCallback((name) => ({
    name,
    value: values[name] ?? "",
    onChange: (e) => {
      const v = e?.target ? e.target.value : e;
      setValue(name, v);
    },
    onBlur: () => markTouched(name),
    error: (touched[name] || submitCount > 0) ? errors[name] : undefined,
  }), [values, errors, touched, submitCount, setValue, markTouched]);

  return useMemo(() => ({
    values, errors, touched, submitting, submitCount,
    setValue, setValues,
    setError, setErrors, clearError,
    markTouched,
    getFieldProps,
    handleSubmit,
    reset,
    isValid: !Object.keys(errors).some((k) => errors[k]),
  }), [
    values, errors, touched, submitting, submitCount,
    setValue, setError, clearError, markTouched, getFieldProps,
    handleSubmit, reset,
  ]);
}


// ── <FormField/> ──────────────────────────────────────────────────────


const FieldContext = createContext(null);

/**
 * Wraps a labelled form control with consistent error + hint + a11y.
 *
 * Usage (controlled input):
 *
 *   <FormField label="Email" name="email" required hint="Te mandamos un código">
 *     <FormField.Input
 *       type="email"
 *       autoComplete="email"
 *       inputMode="email"
 *       {...form.getFieldProps("email")}
 *     />
 *   </FormField>
 *
 * The wrapper auto-generates ``id`` from ``name`` so the label's
 * ``htmlFor`` points to the right control, sets ``aria-invalid``
 * when there's an error, and pipes ``aria-describedby`` to the
 * error / hint elements below.
 */
export function FormField({
  label,
  name,
  required = false,
  hint,
  error,           // optional; if omitted, child <Input> gets error via context
  className = "",
  testid,
  children,
}) {
  const id = `f-${name}`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;
  const ctx = useMemo(() => ({
    id, name, error, describedBy, invalid: !!error,
  }), [id, name, error, describedBy]);
  return (
    <FieldContext.Provider value={ctx}>
      <div
        className={`space-y-1.5 ${className}`}
        data-testid={testid || `form-field-${name}`}
      >
        {label && (
          <Label
            htmlFor={id}
            className="text-xs font-medium text-slate-700 flex items-center gap-1"
          >
            {label}
            {required && (
              <span className="text-rose-500" aria-hidden="true">*</span>
            )}
          </Label>
        )}
        {children}
        {hint && !error && (
          <p id={hintId} className="text-[11px] text-slate-500 leading-snug">
            {hint}
          </p>
        )}
        {error && (
          <p
            id={errorId}
            role="alert"
            className="text-[11px] text-rose-600 font-medium leading-snug form-err-msg"
            data-testid={`form-error-${name}`}
          >
            {error}
          </p>
        )}
      </div>
    </FieldContext.Provider>
  );
}


function useField() {
  const ctx = useContext(FieldContext);
  if (!ctx) {
    throw new Error("FormField subcomponents must be inside <FormField>");
  }
  return ctx;
}


/** Native-Input wrapper that respects the surrounding FormField context. */
FormField.Input = function FormFieldInput({ className = "", error: _ignored, ...props }) {
  const f = useField();
  const errorRing = f.invalid
    ? "border-rose-300 focus-visible:ring-rose-300/40"
    : "";
  return (
    <Input
      id={f.id}
      aria-invalid={f.invalid || undefined}
      aria-describedby={f.describedBy}
      data-testid={`form-input-${f.name}`}
      className={`${errorRing} ${className}`.trim()}
      {...props}
    />
  );
};


/** Textarea variant. */
FormField.Textarea = function FormFieldTextarea({ className = "", error: _ignored, ...props }) {
  const f = useField();
  const errorRing = f.invalid
    ? "border-rose-300 focus-visible:ring-rose-300/40"
    : "";
  return (
    <Textarea
      id={f.id}
      aria-invalid={f.invalid || undefined}
      aria-describedby={f.describedBy}
      data-testid={`form-textarea-${f.name}`}
      className={`${errorRing} ${className}`.trim()}
      {...props}
    />
  );
};


/**
 * Currency-style numeric input. Sets ``inputMode="decimal"`` so mobile
 * keyboards land on the numeric pad, prepends an optional currency
 * badge, and accepts only digits + one separator on the client side.
 * Still controlled — the caller manages ``value`` as a string.
 */
FormField.Currency = function FormFieldCurrency({
  className = "",
  currency,
  value,
  onChange,
  ...props
}) {
  const f = useField();
  const errorRing = f.invalid
    ? "border-rose-300 focus-visible:ring-rose-300/40"
    : "";
  const handleChange = (e) => {
    const raw = e.target.value;
    // Allow empty, digits, optional single dot/comma, optional decimals.
    const ok = /^$|^[0-9]+(?:[.,][0-9]*)?$/.test(raw);
    if (!ok) return;  // silently reject (no flicker, no toast).
    onChange?.({ target: { value: raw, name: f.name } });
  };
  return (
    <div className="relative">
      {currency && (
        <span
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[11px] font-semibold text-slate-500 pointer-events-none"
          aria-hidden="true"
        >
          {currency}
        </span>
      )}
      <Input
        id={f.id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        aria-invalid={f.invalid || undefined}
        aria-describedby={f.describedBy}
        value={value ?? ""}
        onChange={handleChange}
        data-testid={`form-currency-${f.name}`}
        className={`${currency ? "pl-12" : ""} ${errorRing} ${className}`.trim()}
        {...props}
      />
    </div>
  );
};


// ── <SubmitButton/> ───────────────────────────────────────────────────


/**
 * Submit button that:
 *   • Auto-disables while ``submitting`` is true (prevents double-clicks).
 *   • Shows a spinner alongside the label.
 *   • Optionally honours ``isValid`` to stay disabled with invalid forms.
 *
 * Defaults to ``type="submit"``; pair with any <form onSubmit={…}>.
 */
export function SubmitButton({
  submitting = false,
  isValid = true,
  disabled = false,
  children,
  loadingText,
  className = "",
  testid = "form-submit",
  ...rest
}) {
  const lock = submitting || disabled || isValid === false;
  return (
    <Button
      type="submit"
      disabled={lock}
      aria-busy={submitting || undefined}
      data-testid={testid}
      className={`gap-2 ${className}`.trim()}
      {...rest}
    >
      {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
      <span>{submitting && loadingText ? loadingText : children}</span>
    </Button>
  );
}


// ── useZodForm — convenience wrapper around useFormUX + Zod ─────────
//
// Thin sugar that lets callers pass a Zod schema as the *first* argument
// instead of nesting it under ``options.validate``. Identical return
// shape to ``useFormUX``.
//
//   const form = useZodForm(Schema, { email: "" });
//
// Equivalent to:
//   const form = useFormUX({ email: "" }, { validate: Schema });
//
// Why a wrapper? Removes one indentation level + nudges new code toward
// the typed-validation path without breaking existing call sites.
export function useZodForm(schema, initialValues, options = {}) {
  return useFormUX(initialValues, { ...options, validate: schema });
}


export default {
  parseApiError,
  useFormUX,
  useZodForm,
  FormField,
  SubmitButton,
};
